import { useCallback, useContext, useEffect, useRef, useState } from 'react'

import { AuthorBadge, theme } from '@hhgtech/hhg-components'
import { Avatar, Spoiler, Text } from '@hhgtech/hhg-components/mantine'
import { useSSOV2Store } from '@hhgtech/hhg-components/ssoV2'
import { Box } from '@mantine/core'
import { useIntl } from 'react-intl'

import { callApiNew } from 'api/axios'
import { PATHS } from 'api/paths'
import {
  getCurrentBaseUrl,
  getHelloSitesUrl,
  getUrlToRedirectToSSOWithReturnUrl,
} from 'api/utils/paths'
import { redirectToSSOWithReturnUrl } from 'api/utils/redirects'
import { useCurrentUrl } from 'hooks/useCurrentUrl'
import { useIsMobile } from 'hooks/useIsMobile'
import { useRouterExtend } from 'hooks/useRouterExtend'
import {
  DEFAULT_ANONYMOUS_AVATAR_IMG,
  IS_SSOV2_ENABLED,
} from 'interfaces/constants'
import { TogetherPost } from 'interfaces/types'
import { AppContext } from 'state/appContext'
import { decodeMention, decodePostUrl } from 'utils/post'

import { StyledPost } from './index.styled'

export const Post = ({
  post,
  communitySlug,
}: {
  post: TogetherPost
  communitySlug?: string
}) => {
  const {
    state: { isLoggedIn },
  } = useContext(AppContext)
  const { formatMessage: f } = useIntl()
  const { locale } = useRouterExtend()
  const currentUrl = useCurrentUrl()
  const isMobile = useIsMobile()
  const spoilerControlRef = useRef<HTMLButtonElement>(null)
  const [isLike, setIsLike] = useState(post.liked)
  const [isTrigger, setIsTrigger] = useState(false)
  const description = decodeMention(
    decodePostUrl(post.description || '', 'display'),
    post.mentions || [],
  )

  const name = post?.isAnonymous
    ? f({
        id: 'cardPost.anonymous',
        defaultMessage: 'Anonymous',
      })
    : post.author?.name || post.author?.username || ''

  const avatar = post?.isAnonymous
    ? DEFAULT_ANONYMOUS_AVATAR_IMG
    : post.author?.avatar
  const authorRole = post?.isAnonymous ? undefined : post.author?.role

  const relativePostUrl = `/community/${communitySlug}/${post?.slug}`
  const handleClick = (e?: React.MouseEvent<HTMLAnchorElement>) => {
    e?.preventDefault()
    if (!isLoggedIn) {
      if (IS_SSOV2_ENABLED) {
        useSSOV2Store.getState().triggerLogin({
          returnUrl: `${getHelloSitesUrl(
            locale,
          )}/community/on-boarding/?returnUrl=/${communitySlug}/${
            post?.slug
          }?source=communities&action=embeddedarticle&campaign=${communitySlug}/${
            post?.slug
          }`,
        })
      } else {
        window.location.href = getUrlToRedirectToSSOWithReturnUrl(
          `${getHelloSitesUrl(
            locale,
          )}/community/on-boarding/?returnUrl=/${communitySlug}/${
            post?.slug
          }?source=communities&action=embeddedarticle&campaign=${communitySlug}/${
            post?.slug
          }`,
        )
      }
    } else {
      window.location.href = `/community/${communitySlug}/${post?.slug}`
    }
  }

  const handleLikeClick = async () => {
    if (!isLoggedIn) {
      if (IS_SSOV2_ENABLED) {
        useSSOV2Store.getState().triggerLogin({
          returnUrl: `${getHelloSitesUrl(
            locale,
          )}/community/on-boarding/?returnUrl=${encodeURIComponent(
            `/${communitySlug}/${post?.slug}?postAction=like`,
          )}`,
        })
      } else {
        window.location.href = getUrlToRedirectToSSOWithReturnUrl(
          `${getHelloSitesUrl(
            locale,
          )}/community/on-boarding/?returnUrl=${encodeURIComponent(
            `/${communitySlug}/${post?.slug}?postAction=like`,
          )}`,
        )
      }
    } else {
      await callApiNew(
        PATHS.TOGETHER.POST_TOGETHER_LIKE_POST,
        {
          params: {
            postId: post.id,
          },
        },
        'together',
      )
      setIsLike((pre) => !pre)
      setIsTrigger(true)
    }
  }
  const [likeCount, setLikeCount] = useState<number>(post?.likesCount)
  useEffect(() => {
    if (isTrigger) {
      isLike ? setLikeCount((pre) => ++pre) : setLikeCount((pre) => --pre)
    }
  }, [post?.likesCount, isTrigger, isLike])
  const spoilerHandler = useCallback((e: any) => {
    if (!isLoggedIn) {
      e.preventDefault()
      e.stopPropagation()
      redirectToSSOWithReturnUrl(
        `${getCurrentBaseUrl(
          locale,
        )}/community/on-boarding?return=${encodeURIComponent(
          (relativePostUrl || '').replace('/community', ''),
        )}`,
        'communities',
      )
    }
  }, [])
  useEffect(() => {
    setTimeout(
      () =>
        spoilerControlRef.current?.addEventListener('click', spoilerHandler, {
          passive: true,
        }),
      1000,
    )

    return () =>
      spoilerControlRef.current?.removeEventListener('click', spoilerHandler)
  }, [])

  return (
    <StyledPost>
      <div className="header">
        <Avatar
          src={avatar}
          radius="xl"
          size={40}
          color={theme.colors.primaryHover}
        >
          {(name?.[0] || '').toUpperCase()}
        </Avatar>
        <div className="detail">
          <Box
            component="a"
            href={`/community/${communitySlug}/${post?.slug}`}
            data-event-category="Together"
            data-event-action="Click Post"
            data-event-label={`${getHelloSitesUrl(
              locale,
            )}/community/${communitySlug}/${post?.slug}`}
            className="pointer-event-child-none"
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              window.location.href = `${getHelloSitesUrl(
                locale,
              )}${relativePostUrl}`
            }}
          >
            <Text size="s3">{post.title}</Text>
          </Box>
          <div className="detail-author">
            <Text as="span" size="p4" color={theme.colors.gray600}>
              {name}
            </Text>
            {!isMobile && <AuthorBadge role={authorRole || ''} />}
            <Text as="span" size="p4" color={theme.colors.gray600}>
              ·
            </Text>
            <Text as="span" size="p4" color={theme.colors.gray600}>
              {post.createdTime}
            </Text>
          </div>
        </div>
      </div>
      <div className="content">
        <Spoiler
          key={String(isMobile)}
          line={3}
          lineHeight={isMobile ? 22 : 24}
          trackingAttributes={{
            dataEventCategory: 'Together',
            dataEventAction: 'See More Click',
            dataEventLabel: currentUrl,
          }}
          controlRef={spoilerControlRef}
        >
          <Text
            as="div"
            size="p3"
            color={theme.colors.gray800}
            dangerouslySetInnerHTML={{ __html: description }}
          ></Text>
        </Spoiler>
      </div>
      <div className="action">
        <div
          data-event-category="Together"
          data-event-action="Like Click"
          data-event-label={`${getHelloSitesUrl(
            locale,
          )}/community/${communitySlug}/${post?.slug}`}
          onClick={handleLikeClick}
          className="pointer-event-child-none"
        >
          {isLike ? (
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="#d84023"
              className="txt-icon"
            >
              <path
                d="M19.8757 5.41452C19.4289 4.96607 18.8984 4.61034 18.3145 4.36763C17.7307 4.12492 17.1048 4 16.4728 4C15.8408 4 15.215 4.12492 14.6311 4.36763C14.0472 4.61034 13.5167 4.96607 13.0699 5.41452L12.1426 6.34476L11.2154 5.41452C10.3128 4.50912 9.08878 4.00047 7.81244 4.00047C6.5361 4.00047 5.31204 4.50912 4.40953 5.41452C3.50702 6.31992 3 7.5479 3 8.82833C3 10.1088 3.50702 11.3367 4.40953 12.2421L5.3368 13.1724L12.1426 20L18.9484 13.1724L19.8757 12.2421C20.3227 11.7939 20.6773 11.2617 20.9193 10.676C21.1612 10.0902 21.2857 9.46237 21.2857 8.82833C21.2857 8.19428 21.1612 7.56645 20.9193 6.9807C20.6773 6.39494 20.3227 5.86275 19.8757 5.41452V5.41452Z"
                stroke="#d84023"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              ></path>
            </svg>
          ) : (
            <svg width="24" height="24" viewBox="0 0 20 20" fill="none">
              <path
                d="M13.7498 2.5C11.3373 2.5 9.99984 4.24167 9.99984 4.24167C9.99984 4.24167 8.66234 2.5 6.24984 2.5C3.71817 2.5 1.6665 4.55167 1.6665 7.08333C1.6665 10.5592 5.75984 13.9275 6.90067 14.9917C7.46747 15.52 8.12184 16.1134 8.68282 16.6176C9.43278 17.2915 10.5669 17.2915 11.3169 16.6176C11.8778 16.1134 12.5322 15.52 13.099 14.9917C14.2398 13.9275 18.3332 10.5592 18.3332 7.08333C18.3332 4.55167 16.2815 2.5 13.7498 2.5ZM12.3423 13.425C12.1948 13.5583 12.0665 13.6742 11.9623 13.7717C11.3365 14.355 10.5965 15.0242 9.99984 15.5592C9.40317 15.0242 8.66234 14.3542 8.03734 13.7717C7.93234 13.6742 7.804 13.5575 7.65734 13.425C6.474 12.3558 3.33317 9.51583 3.33317 7.08333C3.33317 5.475 4.6415 4.16667 6.24984 4.16667C7.77234 4.16667 8.65484 5.22917 8.67817 5.25667L9.99984 6.66667L11.3215 5.25667C11.3298 5.24583 12.2273 4.16667 13.7498 4.16667C15.3582 4.16667 16.6665 5.475 16.6665 7.08333C16.6665 9.51583 13.5257 12.3558 12.3423 13.425Z"
                fill="#595959"
              />
            </svg>
          )}
          <Text size="p4" color={theme.colors.gray600}>
            {likeCount}
          </Text>
        </div>
        <a
          data-event-category="Together"
          data-event-action="Comment Icon Click"
          data-event-label={`${getHelloSitesUrl(
            locale,
          )}/community/${communitySlug}/${post?.slug}`}
          href={relativePostUrl + '#comment'}
          className="pointer-event-child-none"
          onClick={handleClick}
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path
              d="M7.49984 9.1665C7.49984 9.62674 7.12674 9.99984 6.6665 9.99984C6.20627 9.99984 5.83317 9.62674 5.83317 9.1665C5.83317 8.70627 6.20627 8.33317 6.6665 8.33317C7.12674 8.33317 7.49984 8.70627 7.49984 9.1665Z"
              fill="#595959"
            />
            <path
              d="M9.99984 9.99984C10.4601 9.99984 10.8332 9.62674 10.8332 9.1665C10.8332 8.70627 10.4601 8.33317 9.99984 8.33317C9.5396 8.33317 9.1665 8.70627 9.1665 9.1665C9.1665 9.62674 9.5396 9.99984 9.99984 9.99984Z"
              fill="#595959"
            />
            <path
              d="M13.3332 9.99984C13.7934 9.99984 14.1665 9.62674 14.1665 9.1665C14.1665 8.70627 13.7934 8.33317 13.3332 8.33317C12.8729 8.33317 12.4998 8.70627 12.4998 9.1665C12.4998 9.62674 12.8729 9.99984 13.3332 9.99984Z"
              fill="#595959"
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M9.99984 1.6665C5.45702 1.6665 1.6665 4.9782 1.6665 9.1665C1.6665 13.3548 5.45702 16.6665 9.99984 16.6665C10.1871 16.6665 10.3649 16.6421 10.5429 16.6177C10.639 16.6045 10.7353 16.5912 10.8332 16.5819V18.4824C10.8332 18.8119 11.1975 19.011 11.4748 18.8332L12.1174 18.4211C13.9192 17.2646 17.8207 14.2627 18.286 9.95426C18.3155 9.69796 18.3332 9.43546 18.3332 9.1665C18.3332 4.9782 14.5427 1.6665 9.99984 1.6665ZM9.99984 3.33317C13.742 3.33317 16.6665 5.99148 16.6665 9.1665C16.6665 9.367 16.6541 9.56703 16.6307 9.76872V9.77197L16.6291 9.77523C16.3411 12.4424 14.2061 14.5372 12.4998 15.9032V15.6613C12.4998 15.1351 12.0178 14.7409 11.5021 14.8452C11.0141 14.9437 10.5126 14.9998 9.99984 14.9998C6.25766 14.9998 3.33317 12.3415 3.33317 9.1665C3.33317 5.99148 6.25766 3.33317 9.99984 3.33317Z"
              fill="#595959"
            />
          </svg>
          <Text size="p4" color={theme.colors.gray600}>
            {post?.commentsCount}
          </Text>
        </a>
      </div>
    </StyledPost>
  )
}
